import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../App';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import Watermark from '../components/Watermark';

const BOOKMAKER_LABELS = {
  betway_bw: 'Betway',
  pstbets_bw: 'PST Bets',
  sunbet_bw: 'Sunbet',
  bet267_bw: 'Bet267',
  bbets_bw: 'BBets',
  betxplosion_bw: 'BetXplosion',
};

const BOOKMAKER_COLORS = {
  betway_bw: '#00a651',
  pstbets_bw: '#1a56db',
  sunbet_bw: '#f59e0b',
  bet267_bw: '#8b5cf6',
  bbets_bw: '#ec4899',
  betxplosion_bw: '#f97316',
};

// Markets that contain "winner"/"match" in the name but are NOT the full-time result
const NOT_FULL_TIME = /half|handicap|draw-no-bet|draw no bet|dnb|double.?chance|1st|2nd|first|second|period|quarter|set\b/i;

// swapped = this bookmaker lists the teams the other way round, so its home is our away
function get1x2(markets, swapped = false) {
  const TYPES = ['winner3', 'win-draw-win', 'match', 'fs', '1x2', 'winner'];
  const m =
    markets?.find(m => m.marketType?.toLowerCase() === 'winner3') ||
    markets?.find(m => {
      const type = (m.marketType || '').toLowerCase();
      const name = (m.marketName || '').toLowerCase();
      if (NOT_FULL_TIME.test(type) || NOT_FULL_TIME.test(name)) return false;
      return TYPES.some(t => type.includes(t) || name.includes(t));
    });
  if (!m) return null;

  const outcomes = m.outcomes || [];
  const DRAW_NAMES = ['draw', 'x', 'ot_cross', 'tie'];
  const HOME_NAMES = ['home', '1', 'ot_one'];
  const AWAY_NAMES = ['away', '2', 'ot_two'];

  const d = outcomes.find(o => DRAW_NAMES.includes(o.name?.toLowerCase()))?.price;

  // Try standard names first
  let h = outcomes.find(o => HOME_NAMES.includes(o.name?.toLowerCase()))?.price;
  let a = outcomes.find(o => AWAY_NAMES.includes(o.name?.toLowerCase()))?.price;

  // Fallback — use non-draw outcomes in order (for Betway live team names)
  if (!h) {
    const nonDraw = outcomes.filter(o => !DRAW_NAMES.includes(o.name?.toLowerCase()));
    h = nonDraw[0]?.price;
    a = nonDraw[1]?.price;
  }

  if (!h) return null;
  return swapped ? { home: a, draw: d, away: h } : { home: h, draw: d, away: a };
}

// Matched with low confidence, so its prices must not be used to claim an arbitrage
function isDoubtful(bmOdds) {
  return bmOdds && (bmOdds.flagged === true || (typeof bmOdds.confidence === 'number' && bmOdds.confidence < 90));
}

function formatTime(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleTimeString('en-BW', { hour: '2-digit', minute: '2-digit' });
}

function OddsChart({ history, homeTeam, awayTeam }) {
  if (!history || Object.keys(history).length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>
        Not enough odds history yet — check back soon
      </div>
    );
  }

  // Check if any bookmaker has 2+ points
  const hasEnough = Object.values(history).some(h => h.length >= 2);
  if (!hasEnough) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>
        Not enough odds history yet — check back soon
      </div>
    );
  }

  // Build unified timeline — merge all timestamps
  const allTimestamps = [...new Set(
    Object.values(history).flatMap(h => h.map(p => p.timestamp))
  )].sort();

  const data = allTimestamps.map(ts => {
    const point = { time: formatTime(ts) };
    Object.entries(history).forEach(([bm, bmHistory]) => {
      const p = bmHistory.find(p => p.timestamp === ts);
      if (p) {
        point[`${bm}_home`] = p.home;
        point[`${bm}_away`] = p.away;
      }
    });
    return point;
  });

  const bookmakers = Object.keys(history);
  const kickoffTs = Object.values(history).flatMap(h => h.filter(p => p.isLive)).map(p => p.timestamp).sort()[0];
  const kickoffTime = kickoffTs ? formatTime(kickoffTs) : null;

  return (
    <div style={{ padding: '12px 0' }}>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {bookmakers.map(bm => (
          <span key={bm} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 20, height: 2, background: BOOKMAKER_COLORS[bm], display: 'inline-block' }}></span>
            {BOOKMAKER_LABELS[bm]}
          </span>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
          <XAxis dataKey="time" tick={{ fontSize: 10, fill: 'var(--text3)' }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 10, fill: 'var(--text3)' }} domain={['auto', 'auto']} />
          <Tooltip
            contentStyle={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
            labelStyle={{ color: 'var(--text2)', fontWeight: 600 }}
          />
          {kickoffTime && (
            <ReferenceLine x={kickoffTime} stroke="var(--accent)" strokeDasharray="4 2" label={{ value: 'KO', fill: 'var(--accent)', fontSize: 10 }} />
          )}
          {bookmakers.map(bm => (
            <Line
              key={`${bm}_home`}
              type="monotone"
              dataKey={`${bm}_home`}
              name={`${BOOKMAKER_LABELS[bm]} (H)`}
              stroke={BOOKMAKER_COLORS[bm]}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4, textAlign: 'center' }}>
        Showing home win odds movement per bookmaker
      </div>
    </div>
  );
}

function MatchCard({ match, isMobile }) {
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const bookmakers = Object.keys(match.bookmakers || {});
  const odds = match.bestOdds;

  async function loadHistory() {
  if (history) return;
  setLoadingHistory(true);
  try {
    // Build history from bookmaker data already in match
    const bookmakerHistories = {};
    Object.entries(match.bookmakers || {}).forEach(([bm, bmOdds]) => {
      if (bmOdds.oddsHistory && bmOdds.oddsHistory.length > 0) {
        bookmakerHistories[bm] = bmOdds.oddsHistory;
      }
    });
    setHistory(bookmakerHistories);
  } catch {
    setHistory({});
  } finally {
    setLoadingHistory(false);
  }
}

  function handleExpand() {
    setExpanded(e => !e);
    if (!expanded) loadHistory();
  }

  const hasArb = match.arbOpportunity;

  return (
    <div style={{
      borderBottom: '1px solid var(--border)',
      background: expanded ? 'var(--bg3)' : 'transparent',
      transition: 'background .12s',
    }}>
      {/* Match row */}
      <div
        onClick={handleExpand}
        style={{ padding: isMobile ? '12px 14px' : '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: isMobile ? 13 : 14 }}>
              {match.homeTeam} <span style={{ color: 'var(--text3)', fontWeight: 400 }}>vs</span> {match.awayTeam}
            </span>
            {hasArb && <span className="pill pill-green" style={{ fontSize: 9 }}>⚡ ARB</span>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>
            {match.league} · {bookmakers.length} bookmakers
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {bookmakers.map(bm => (
              <span key={bm} style={{
                fontSize: 10, padding: '2px 6px', borderRadius: 4,
                background: `${BOOKMAKER_COLORS[bm]}22`,
                color: BOOKMAKER_COLORS[bm],
                fontWeight: 600,
              }}>
                {BOOKMAKER_LABELS[bm] || bm}
              </span>
            ))}
          </div>
        </div>

        {/* Best odds */}
        {odds && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>Best odds</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {[
                { label: '1', value: odds.home?.price, bm: odds.home?.bookmaker },
                { label: 'X', value: odds.draw?.price, bm: odds.draw?.bookmaker },
                { label: '2', value: odds.away?.price, bm: odds.away?.bookmaker },
              ].map(({ label, value, bm }) => value ? (
                <div key={label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: 'var(--text3)' }}>{label}</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--accent)' }}>{value}</div>
                  <div style={{ fontSize: 9, color: BOOKMAKER_COLORS[bm] }}>{BOOKMAKER_LABELS[bm]?.split(' ')[0]}</div>
                </div>
              ) : null)}
            </div>
          </div>
        )}

        <div style={{ color: 'var(--text3)', fontSize: 12, flexShrink: 0 }}>
          {expanded ? '▲' : '▼'}
        </div>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div style={{ padding: isMobile ? '0 14px 14px' : '0 20px 16px' }}>

          {/* Odds comparison table */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>
              Odds Comparison
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>Bookmaker</th>
                    <th style={{ padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>Home</th>
                    <th style={{ padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>Draw</th>
                    <th style={{ padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11 }}>Away</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(match.bookmakers || {}).map(([bm, bmOdds]) => {
                    const o = get1x2(bmOdds.markets, bmOdds.swapped === true);
                    if (!o) return null;
                    return (
                      <tr key={bm} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: BOOKMAKER_COLORS[bm], flexShrink: 0 }}></span>
                          <span style={{ color: BOOKMAKER_COLORS[bm], fontWeight: 600 }}>{BOOKMAKER_LABELS[bm] || bm}</span>
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, color: odds?.home?.bookmaker === bm ? 'var(--success)' : 'var(--text)' }}>
                          {o.home ?? '—'}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, color: odds?.draw?.bookmaker === bm ? 'var(--success)' : 'var(--text)' }}>
                          {o.draw ?? '—'}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, color: odds?.away?.bookmaker === bm ? 'var(--success)' : 'var(--text)' }}>
                          {o.away ?? '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 10, color: 'var(--success)', marginTop: 4 }}>✓ Green = best odds for that outcome</div>
          </div>

          {/* Odds history chart */}
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 4 }}>
              Odds Movement
            </div>
            {loadingHistory ? (
              <div className="skeleton" style={{ height: 200 }} />
            ) : (
              <OddsChart history={history} homeTeam={match.homeTeam} awayTeam={match.awayTeam} bookmakers={Object.keys(match.bookmakers || {})} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LiveOdds() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);
  const [width, setWidth] = useState(window.innerWidth);
  const intervalRef = useRef(null);

  const isMobile = width < 768;

  // How many different bookmakers are actually in the data right now
  const bookmakerTotal = new Set(matches.flatMap(m => Object.keys(m.bookmakers || {}))).size;

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const fetchMatches = useCallback(async () => {
    try {
      const data = await apiFetch('/live-odds');
      const { matches = [] } = data;

      // Filter virtual events and process matched events
const VIRTUAL_PREFIXES = ['z.', 'zwc.', 'zturbo.', 'srl'];
const filtered = matches
  .filter(m => {
    const home = m.homeTeam?.toLowerCase() || '';
    const away = m.awayTeam?.toLowerCase() || '';
    return !VIRTUAL_PREFIXES.some(p => home.startsWith(p) || away.startsWith(p)) &&
           !home.endsWith(' srl') && !away.endsWith(' srl');
  })
  .map(m => {
    const bestOdds = { home: null, draw: null, away: null };
    let arbOpportunity = false;

    const doubtful = new Set();
    Object.entries(m.bookmakerOdds || {}).forEach(([bm, bmOdds]) => {
      const o = get1x2(bmOdds.markets, bmOdds.swapped === true);
      if (o) {
        if (isDoubtful(bmOdds)) doubtful.add(bm);
        if (o.home && (!bestOdds.home || o.home > bestOdds.home.price)) bestOdds.home = { price: o.home, bookmaker: bm };
        if (o.draw && (!bestOdds.draw || o.draw > bestOdds.draw.price)) bestOdds.draw = { price: o.draw, bookmaker: bm };
        if (o.away && (!bestOdds.away || o.away > bestOdds.away.price)) bestOdds.away = { price: o.away, bookmaker: bm };
      }
    });

    if (bestOdds.home && bestOdds.draw && bestOdds.away) {
      const implied = (1 / bestOdds.home.price) + (1 / bestOdds.draw.price) + (1 / bestOdds.away.price);
      const books = [bestOdds.home.bookmaker, bestOdds.draw.bookmaker, bestOdds.away.bookmaker];
      // A real arbitrage needs 2+ bookmakers, a believable margin, and no doubtful match
      arbOpportunity = implied < 1 && implied >= 0.8 && new Set(books).size >= 2 && !books.some(b => doubtful.has(b));
    }

    return {
      eventId: m._id,
      homeTeam: m.homeTeam,
      awayTeam: m.awayTeam,
      league: m.league,
      startTime: m.startTime,
      bookmakers: m.bookmakerOdds || {},
      bestOdds,
      arbOpportunity,
      bookmakerCount: m.bookmakerCount,
    };
  })
    .sort((a, b) => b.bookmakerCount - a.bookmakerCount);

// Deduplicate by home+away team
const seen = new Set();
const deduped = filtered.filter(m => {
  const key = `${m.homeTeam}|${m.awayTeam}`;
  if (seen.has(key)) return false;
  seen.add(key);
  return true;
});

setMatches(deduped);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError('Could not load live odds. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMatches();
    intervalRef.current = setInterval(() => {
      if (!document.hidden) fetchMatches();
    }, 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchMatches]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Watermark />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: isMobile ? 16 : 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--success)', boxShadow: '0 0 6px var(--success)', display: 'inline-block' }}></span>
            Live Football Odds
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            Real-time odds from {bookmakerTotal || '—'} bookmakers · Auto-refreshes every 30s
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastRefresh && <span style={{ fontSize: 11, color: 'var(--text3)' }}>Updated {lastRefresh.toLocaleTimeString()}</span>}
          <button className="btn btn-outline" style={{ fontSize: 11, padding: '5px 12px' }} onClick={fetchMatches} disabled={loading}>
            {loading ? '...' : '↺ Refresh'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
        <div className="stat-card" style={{ '--accent-color': 'var(--success)', textAlign: 'center' }}>
          <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>Live Matches</div>
          <div className="stat-value" style={{ fontSize: isMobile ? 18 : 24, textAlign: 'center' }}>{loading ? '—' : matches.length}</div>
          <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center' }}>football only</div>
        </div>
        <div className="stat-card" style={{ '--accent-color': 'var(--accent)', textAlign: 'center' }}>
          <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>Arb Opportunities</div>
          <div className="stat-value" style={{ fontSize: isMobile ? 18 : 24, textAlign: 'center', color: 'var(--success)' }}>
            {loading ? '—' : matches.filter(m => m.arbOpportunity).length}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center' }}>live right now</div>
        </div>
        <div className="stat-card" style={{ '--accent-color': 'var(--accent2)', textAlign: 'center' }}>
          <div className="stat-label" style={{ fontSize: 10, textAlign: 'center' }}>Bookmakers</div>
          <div className="stat-value" style={{ fontSize: isMobile ? 18 : 24, textAlign: 'center' }}>{loading ? '—' : bookmakerTotal}</div>
          <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center' }}>sources</div>
        </div>
      </div>

      {/* Matches */}
      <div className="card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 20 }}>
            {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: 80, marginBottom: 10 }} />)}
          </div>
        ) : error ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--danger)' }}>{error}</div>
        ) : matches.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">⚽</div>
            <p>No live football matches right now</p>
            <p style={{ fontSize: 12, marginTop: 6 }}>Check back when matches are in play</p>
          </div>
        ) : (
          matches.map((match, i) => (
            <MatchCard key={match.eventId || i} match={match} isMobile={isMobile} />
          ))
        )}
      </div>

      <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center' }}>
        ⚠️ Live odds update every 30 seconds. Always verify with bookmaker before placing bets.
      </div>
    </div>
  );
}